<!-- DashboardLayout.vue -->

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { storeToRefs } from "pinia";
// ✅ NOVO: usado para formatar previousEndDate vinda do polling (UTC ISO)
// em "dd/MM/yyyy" no fuso local do usuário. Mesma dependência que o
// AccountSummary já usa, então o pacote já está no projeto.
import { format } from "date-fns";

import AccountSettingsModal from "@/components/Profile/AccountSettingsModal.vue";
import NotificationBell from "@/components/Notifications/NotificationBell.vue";
import UpgradePage from "@/components/User/UpgradePage.vue";
import SettingsModal from "@/components/Setting/SettingsModal.vue";

import SupportEmailModal from "@/components/SupportEmailModal.vue"; // ✅ NOVO
import FounderWelcomeModal from "@/components/FounderWelcomeModal.vue";
import { useFounderContact } from "@/composables/useFounderContact";

import SubscriptionExpiredModal from "@/components/Subscription/SubscriptionExpiredModal.vue";

import { sendSupportMessage } from "@/services/supportService";

import { useUserStore } from "@/stores/useUserStore";
import { usePlanStore } from "@/stores/usePlanStore";
import { useSubscriptionStore } from "@/stores/useSubscriptionStore";

import RecurringPaymentRejectedModal from "@/components/Subscription/RecurringPaymentRejectedModal.vue";
import { useSubscriptionStatusStore } from "@/stores/useSubscriptionStatusStore";

// Delegação do vínculo: decide se o menu "Membros" aparece para quem está
// logado como membro gerenciado (professor/administrador que monta turma).
import { useMemberRestrictions } from "@/composables/useMemberRestrictions";
import { useTheme } from "@/composables/useTheme";

import api from "@/services/api";

const { canManageCircle } = useMemberRestrictions();

const userStore = useUserStore();
const planStore = usePlanStore();
const subscriptionStore = useSubscriptionStore();

const router = useRouter();
const route = useRoute();

const { plans } = storeToRefs(planStore);

// ✅ MUDANÇA: também expomos latestStatusDetail da store
const { subscriptions } = storeToRefs(subscriptionStore);

const { subscriptionExpiredMessage } = storeToRefs(userStore);

const isDropdownOpen = ref(false);

// Tema claro/escuro: um clique alterna; a classe `dark` em <html> propaga
// para todos os tokens (ver src/assets/tokens.css).
const { isDark, toggleTheme } = useTheme();
const dropdownRef = ref(null);
const showAccountModal = ref(false);
const showUpgradeModal = ref(false);
const showSettingsModal = ref(false);

// ✅ NOVO: controla exibição do modal de Ajuda & Sugestões
const showSupportEmailModal = ref(false);

const showSubscriptionExpiredModal = ref(false);

// 👉 controla se a sidebar está colapsada ou não
const isSidebarCollapsed = ref(false);

// ✅ NOVO: contador incrementado a cada vez que o UpgradeModal (catálogo de
// planos) é fechado.
//
// Contexto do bug que isso resolve:
// O AccountSettingsModal já declarava o prop `upgradeModalClosedSignal`, e o
// AccountSummary já tem a lógica de reagir a ele via :reload-signal. Mas o
// DashboardLayout nunca passava esse valor — o fio estava solto. Sem ele, ao
// fazer upgrade com o AccountSettingsModal aberto por baixo, o AccountSummary
// permanecia montado e exibia dados stale do plano anterior.
//
// Agora, sempre que o UpgradeModal fecha, incrementamos este contador. O
// AccountSettingsModal o repassa ao AccountSummary, que dispara fetchData(true).
const upgradeModalClosedSignal = ref(0);

/**
 * Fonte única reativa para saber qual é o plano/forma de pagamento atual.
 */
const currentSubscriptionInfo = ref({
  planId: null,
  paymentMethod: null,
});

// ✅ NOVO: cache local da mensagem para o modal não perder o texto
//         quando subscriptionExpiredMessage for zerado na store
const cachedExpiredMessage = ref("");

// ====================================================================
// ✅ TOAST PADRÃO (clonado do AddPermissionForm.vue / AddActivityForm.vue)
//    - 3 segundos na tela
//    - centralizado no topo
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

// ✅ NOVO: marca se, NESTA SESSÃO, houve downgrade automático para Free.
// Usado para forçar a badge a refletir o plano gratuito imediatamente.
const downgradedToFreeOnThisSession = ref(false);

const subscriptionStatusStore = useSubscriptionStatusStore();
const showRecurringRejectedModal = ref(false);

// ✅ NOVO: helper compartilhado pelos watchers de downgrade e pelo evento
// 'subscriptionChanged' emitido pelo AccountSummary.
//
// Quando o polling detecta downgrade (expiração natural OU cobrança recusada),
// o backend já atualizou o banco: sub anterior virou Expired/Cancelled e uma
// nova sub Free foi atribuída. O subscriptionStatusStore (que faz o polling)
// reflete isso no próprio state, mas as demais stores que o dashboard consome
// ficam stale:
//
//   - userStore.currentUser ainda mostra planName="Plus" e cotas antigas.
//   - subscriptionStore.subscriptions ainda lista a sub Plus como ativa.
//
// Sem refresh, o AccountSummary continua exibindo "Plano Atual: Plus" e cotas
// do plano antigo, mesmo com o modal de expiração aberto sobre ele. Esta
// função força a sincronização das stores afetadas.
//
// ✅ MUDANÇA: esta mesma função agora também é chamada quando o AccountSummary
// emite 'subscriptionChanged' (cancelar/pausar/reativar/estornar a partir do
// perfil). O nome continua "...AfterDowngrade" por compatibilidade histórica,
// mas o trabalho — recarregar User base, sub ativa e lista de assinaturas —
// é exatamente o necessário para o badge do header voltar a ficar correto
// após qualquer mudança de assinatura feita pelo titular.
//
// As três operações rodam em sequência para garantir ordem:
//   1) refreshCurrentUser  → reload do User base
//   2) fetchCurrentUserSubscription → reaplica planName/cotas a partir da nova
//                                      sub ativa
//   3) fetchSubscriptionsPaged → atualiza a lista que o AccountSummary lê via
//                                 subscriptionStore.activeSubscription
//
// Erros não interrompem o fluxo — só logam.
async function refreshStoresAfterDowngrade() {
  try {
    await userStore.refreshCurrentUser();
    await userStore.fetchCurrentUserSubscription();

    if (userStore.currentUser?.userId) {
      await subscriptionStore.fetchSubscriptionsPaged(
        1,
        100,
        userStore.currentUser.userId
      );
    }
  } catch (err) {
    console.error(
      "[DashboardLayout] Falha ao recarregar stores após mudança de assinatura:",
      err
    );
  } finally {
    // Recalcula o badge do header com base nas stores recém-atualizadas.
    refreshCurrentSubscriptionInfo();
  }
}

// ✅ NOVO: handler do evento 'subscriptionChanged' propagado pelo
// AccountSettingsModal (que por sua vez recebe do AccountSummary).
//
// Disparado quando o titular cancela/pausa/reativa/estorna a própria
// assinatura na aba "Resumo da Conta". O AccountSummary já se reconcilia
// internamente via fetchData(true); este handler garante que as stores
// consumidas pelo DashboardLayout (badge de plano no header) também sejam
// atualizadas — caso contrário o header continuaria mostrando o plano antigo.
function handleSubscriptionChanged() {
  refreshStoresAfterDowngrade();
}

// ✅ MUDANÇA: antes havia UM watcher em `wasRecentlyDowngraded` que SEMPRE
// abria o RecurringPaymentRejectedModal. Isso causava o bug em que uma
// expiração natural (assinatura pausada que chegou ao fim do período)
// disparava o modal errado — porque o backend usava o mesmo flag para
// os dois eventos.
//
// Agora há DOIS watchers, um por flag, alinhados com o DTO do backend:
//
//   wasNaturalExpiration → SubscriptionExpiredModal (já existe, controlado
//                           por showSubscriptionExpiredModal mais abaixo)
//
//   wasPaymentRejected  → RecurringPaymentRejectedModal
//
// Os flags são mutuamente exclusivos por contrato do backend, então no
// máximo um deles é true por poll. Não há risco de abrir os dois modais
// simultaneamente.
//
// Ambos os watchers chamam refreshStoresAfterDowngrade() para que as stores
// que o AccountSummary e demais componentes consomem reflitam o novo plano
// Free imediatamente.
watch(
  () => subscriptionStatusStore.wasPaymentRejected,
  (wasRejected) => {
    if (wasRejected) {
      showRecurringRejectedModal.value = true;
      refreshStoresAfterDowngrade();
    }
  },
  { immediate: true }
);

// ✅ NOVO: watcher dedicado à expiração natural detectada via polling.
//
// Antes, o SubscriptionExpiredModal era disparado APENAS por
// `userStore.subscriptionExpiredMessage`, que só é preenchido no momento
// do login. Isso deixava de fora o cenário onde a assinatura expira
// enquanto o usuário está com a sessão ativa (caso do bug).
//
// Agora o polling também alimenta o modal de expiração: quando o backend
// detecta a expiração (Active+EndDate-vencida OU Expired+NotifiedAt-null),
// retorna wasNaturalExpiration=true em uma única chamada e o modal abre
// na hora. Reutilizamos o mesmo `cachedExpiredMessage`/`showSubscriptionExpiredModal`
// que o fluxo de login já usa, montando a mensagem a partir de
// previousPlanName para manter consistência visual com o modal vindo do login.
watch(
  () => subscriptionStatusStore.wasNaturalExpiration,
  (wasExpired) => {
    if (!wasExpired) return;

    const previousPlan = subscriptionStatusStore.previousPlanName || "anterior";

    // ✅ NOVO: incorpora a data de expiração à frase.
    //
    // O backend envia previousEndDate em UTC (ISO 8601). `new Date(...)` faz
    // o parse e converte automaticamente para o fuso local do navegador;
    // `format(..., "dd/MM/yyyy")` produz a string visível ao usuário.
    //
    // Se por algum motivo previousEndDate vier null/inválida (ex.: backend
    // antigo sem esse campo, ou sub legada sem EndDate), caímos numa frase
    // sem data — preferível a exibir "Invalid Date" ou quebrar o modal.
    const rawEndDate = subscriptionStatusStore.previousEndDate;
    let dateStr = null;
    if (rawEndDate) {
      const parsed = new Date(rawEndDate);
      if (!Number.isNaN(parsed.getTime())) {
        dateStr = format(parsed, "dd/MM/yyyy");
      }
    }

    const message = dateStr
      ? `Sua assinatura ${previousPlan} expirou em ${dateStr} e você foi movido para o plano gratuito.`
      : `Sua assinatura ${previousPlan} expirou e você foi movido para o plano gratuito.`;

    cachedExpiredMessage.value = message;
    showSubscriptionExpiredModal.value = true;
    downgradedToFreeOnThisSession.value = true;

    refreshStoresAfterDowngrade();
  },
  { immediate: true }
);

function handleRecurringRejectedClose() {
  showRecurringRejectedModal.value = false;
  downgradedToFreeOnThisSession.value = true;
}

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 10000); // 3s
}

// ====================================================================

// ----------------- helpers de assinatura -----------------
function pickActiveSubscription(list) {
  const items = Array.isArray(list) ? list : [];
  const actives = items.filter(
    (s) => (s.status || "").toLowerCase() === "active"
  );
  if (actives.length === 0) return null;

  const best = actives.slice().sort((a, b) => {
    const ad = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const bd = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return bd - ad;
  })[0];

  return best || actives[0];
}

function refreshCurrentSubscriptionInfo() {
  const user = userStore.currentUser;

  // ✅ CORREÇÃO: filtra apenas assinaturas do próprio usuário logado
  // evita que filtros do Sys sobre assinaturas de outros usuários
  // alterem o badge do plano exibido no header
  const ownSubscriptions = subscriptions.value?.filter(
    (s) => s.userId === user?.userId
  );

  const active = pickActiveSubscription(ownSubscriptions);
  if (active?.planId) {
    currentSubscriptionInfo.value = {
      planId: active.planId,
      paymentMethod: (active.paymentMethod || "").toLowerCase().trim(),
    };
    return;
  }

  currentSubscriptionInfo.value = {
    planId: user?.planId ?? null,
    paymentMethod: (user?.planName || "").toLowerCase().trim(),
  };
}

// ----------------- nome do plano exibido no chip -----------------
const userPlanName = computed(() => {
  const user = userStore.currentUser;
  if (!user) return "";

  // ✅ PRIORIDADE 0: contexto managed — usa planName do titular vindo do JWT
  if (user.loginContext === "managed") {
    return user.planName || "";
  }

  // ✅ PRIORIDADE 1: downgrade detectado nesta sessão → força badge Free
  if (downgradedToFreeOnThisSession.value) {
    const freePlanFromList =
      plans.value?.find((p) =>
        (p.name || "").trim().toLowerCase().includes("free")
      ) || null;

    if (freePlanFromList) return freePlanFromList.name;
    return "Free";
  }

  // ✅ PRIORIDADE 2: usa a assinatura ativa conhecida (currentSubscriptionInfo)
  const planId = currentSubscriptionInfo.value.planId ?? user.planId;

  if (planId && plans.value?.length) {
    const plan = plans.value.find((p) => String(p.planId) === String(planId));
    if (plan) return plan.name;
  }

  // ✅ PRIORIDADE 3: fallback para o que veio no DTO de login
  return user.planName || "";
});

// ✅ Classe do chip conforme nome do plano
const userPlanClass = computed(() => {
  const raw = (userPlanName.value || "").trim().toLowerCase();

  if (raw.includes("premium")) return "btn-premium";
  if (raw.includes("pro")) return "btn-pro";
  if (raw.includes("plus")) return "btn-plus";
  if (raw.includes("free")) return "btn-free";

  return "btn-free";
});

// ✅ Hint do chip "Membro" no contexto managed
const managedMemberHint = computed(() => {
  const user = userStore.currentUser;

  const managerUserName =
    user?.managerUserName ||
    user?.managerUsername ||
    user?.ownerUserName ||
    user?.ownerUsername ||
    user?.managerName ||
    user?.ownerName ||
    "";

  if (!managerUserName) {
    return "Você está operando como membro";
  }

  return `Você está operando como membro de ${managerUserName}`;
});

// 🔹 Avatar do usuário: só foto real; se não tiver, retorna null
const userAvatarUrl = computed(() => {
  let raw =
    userStore.currentUser?.photoUrl || userStore.currentUser?.photoPath || "";

  // Sem foto → componente mostra ícone
  if (!raw) return null;

  // URL absoluta
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;

  // Monta em cima da base do backend (tirando /api)
  let base = api.defaults.baseURL || "";
  base = base.replace(/\/api$/, "").replace(/\/+$/, "");

  if (!raw.startsWith("/")) raw = "/" + raw;

  return base + raw;
});

const isRightAreaRendered = computed(() => route.name === "RightArea");

function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value;
}

function handleClickOutside(event) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    isDropdownOpen.value = false;
  }
}

const sidebarOpenDropdown = ref([]);

function sidebarToggleDropdown(menu) {
  const index = sidebarOpenDropdown.value.indexOf(menu);
  if (index > -1) {
    sidebarOpenDropdown.value.splice(index, 1);
  } else {
    sidebarOpenDropdown.value.push(menu);
  }
}

// 👉 função para alternar estado da sidebar
function toggleSidebarCollapsed() {
  isSidebarCollapsed.value = !isSidebarCollapsed.value;
}

onMounted(async () => {
  // adicione esta linha:
  console.log(
    "[debug] currentUser ao montar DashboardLayout:",
    JSON.stringify({
      planUsageType: userStore.currentUser?.planUsageType,
      loginContext: userStore.currentUser?.loginContext,
    })
  );

  document.addEventListener("click", handleClickOutside);

  if (!plans.value || plans.value.length === 0) {
    await planStore.fetchActivePlans();
  }

  // ✅ MUDANÇA: só busca assinaturas no contexto owner
  if (
    userStore.currentUser?.userId &&
    userStore.currentUser?.loginContext !== "managed"
  ) {
    await subscriptionStore.fetchSubscriptionsPaged(
      1,
      100,
      userStore.currentUser.userId
    );
  }

  refreshCurrentSubscriptionInfo();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  // evita leak de timeout
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});

// 🔁 mantém o chip sincronizado
//
// ✅ MUDANÇA: antes este watcher observava `subscriptions.value?.length`.
// Isso era frágil: quando uma reconciliação SUBSTITUI uma assinatura por
// outra (ex.: Plus cancelada → Free criada), a contagem permanece 1 → 1 e
// o watcher NÃO disparava, deixando o badge stale.
//
// Agora observamos a IDENTIDADE da assinatura ativa do próprio usuário —
// planId e status. Quando a Plus vira Cancelled e a Free entra como Active,
// `active.planId`/`active.status` mudam mesmo com a contagem constante, e o
// badge recalcula corretamente.
watch(
  () => {
    const own = subscriptions.value?.filter(
      (s) => s.userId === userStore.currentUser?.userId
    );
    const active = pickActiveSubscription(own);
    return [
      userStore.currentUser?.planId,
      userStore.currentUser?.planName,
      active?.planId ?? null,
      active?.status ?? null,
    ];
  },
  () => {
    refreshCurrentSubscriptionInfo();
  }
);

// 🔁 Colapsar fecha os grupos abertos: a coluna estreita mostra apenas os
// ícones dos itens de raiz. Daí em diante quem abre e fecha um submenu é só o
// clique no item que o comanda — colapsada ou não, nada mais mexe nele.
watch(isSidebarCollapsed, (collapsed) => {
  if (collapsed) sidebarOpenDropdown.value = [];
});

// ✅ NOVO: observa a mensagem de expiração vinda do login (useUserStore)
watch(
  () => subscriptionExpiredMessage.value,
  (newMessage) => {
    if (!newMessage) return;

    // Atualiza badge para Free
    const lower = newMessage.toLowerCase();
    if (lower.includes("plano gratuito") || lower.includes("plano free")) {
      downgradedToFreeOnThisSession.value = true;
    }

    cachedExpiredMessage.value = newMessage;
    // Exibe o modal — zera a mensagem na store para não reaparecer
    showSubscriptionExpiredModal.value = true;
    userStore.subscriptionExpiredMessage = null;
  },
  { immediate: true } // ← adicionar isso
);

const logout = () => {
  userStore.logout();
  router.push({ name: "LoginView" });
};

function openAccountModal() {
  showAccountModal.value = true;
  isDropdownOpen.value = false;
}

function openNoteStyleSettingsModal() {
  showSettingsModal.value = true;
  isDropdownOpen.value = false;
}

function openUpgradeModal() {
  showUpgradeModal.value = true;
  isDropdownOpen.value = false;
}

// ── Fale com o fundador ─────────────────────────────────────────────────────
const {
  founderWhatsAppUrl,
  founderFirstName,
  founderWelcomePending,
  dismissFounderWelcome,
} = useFounderContact();

// No primeiro acesso a mensagem aparece dentro do cartão do FirstAccessView,
// antes das opções; nas demais telas, como modal.
const showFounderWelcome = computed(
  () => founderWelcomePending.value && route.name !== "FirstAccessView",
);

function openSupportEmailModal() {
  showSupportEmailModal.value = true;
  // aqui não precisa mexer no dropdown, porque o botão fica na topbar
}

async function handleSupportEmailSubmit(payload) {
  try {
    await sendSupportMessage(payload);

    toastType.value = "success";
    toastMessage.value = "Mensagem enviada com sucesso!";
    showToast.value = true;
    resetToastTimeout();
  } catch (error) {
    console.error("Erro ao enviar mensagem de suporte:", error);
    toastType.value = "error";
    toastMessage.value =
      error?.response?.data?.message ||
      "Ocorreu um erro ao enviar sua mensagem.";
    showToast.value = true;
    resetToastTimeout();
  }
}

function handleCloseUpgradeModal() {
  // ✅ PATCH:
  // Fecha apenas o modal de upgrade/catálogo.
  // Não mexe em showAccountModal, pois o AccountSummary deve continuar aberto.
  showUpgradeModal.value = false;

  // ✅ NOVO: sinaliza o fechamento do UpgradeModal.
  // O contador é repassado ao AccountSettingsModal (prop upgradeModalClosedSignal)
  // e dele ao AccountSummary (prop reload-signal), que então recarrega
  // currentUser/assinaturas/cotas — evitando exibir dados stale do plano
  // anterior após um upgrade bem-sucedido.
  upgradeModalClosedSignal.value += 1;
}

function handleCloseUpgradeFlow() {
  // ✅ PATCH:
  // Fecha o fluxo completo de upgrade, mas ainda assim não fecha
  // o AccountSummary/Profile. Se algum fluxo futuro precisar fechar a conta,
  // isso deve ser feito explicitamente, não por propagação de "close".
  showUpgradeModal.value = false;

  // ✅ NOVO: mesmo motivo de handleCloseUpgradeModal — sinaliza o fechamento
  // para que o AccountSummary recarregue os dados do plano.
  upgradeModalClosedSignal.value += 1;
}
</script>

<template>
  <div id="vue-app-container">
    <aside
      :class="[
 'sidebar-left',
        { 'sidebar-left--collapsed': isSidebarCollapsed },
      ]"
    >
      <div class="sidebar-brand">
        <button
          type="button"
          class="sidebar-brand-link"
          :title="isSidebarCollapsed ? 'Expandir menu' : 'Recolher menu'"
          @click="toggleSidebarCollapsed"
        >
          <img
            src="/litterae_pena_fundo_transparente.png"
            alt="Logo Litterae"
            class="sidebar-brand-logo"
          />
          <span class="sidebar-brand-name">Litterae</span>
        </button>
        <button
          v-if="!isSidebarCollapsed"
          type="button"
          class="sidebar-toggle-btn sidebar-toggle-btn--inline"
          @click="toggleSidebarCollapsed"
          title="Recolher menu"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="3" width="20" height="18" rx="4" style="stroke: var(--text-dark);"/>
            <line x1="10" y1="3" x2="10" y2="21" style="stroke: var(--text-dark);"/>
          </svg>
        </button>
      </div>

      <!-- ✅ Somente esta área rola: da Página Inicial em diante -->
      <nav class="sidebar-nav-scroll">
        <ul class="nav">
          <li class="nav-item nav-item--home">
            <router-link :to="{ name: 'DashboardHome' }" class="nav-link">
              <span>
                <i class="fas fa-th-large"></i>
                <span class="nav-label">Painel geral</span>
              </span>
            </router-link>
          </li>

          <li class="nav-item">
            <a
              href="#"
              class="nav-link"
              @click.prevent="sidebarToggleDropdown('admin')"
              :class="{ active: sidebarOpenDropdown.includes('admin') }"
            >
              <span>
                <i class="fas fa-user-shield"></i>
                <span class="nav-label">Administração</span>
              </span>
              <i
                class="fas fa-chevron-down dropdown-arrow"
                :class="{ collapsed: !sidebarOpenDropdown.includes('admin') }"
              ></i>
            </a>
            <ul v-if="sidebarOpenDropdown.includes('admin')" class="sub-nav">
              <li
                v-if="userStore.currentUser?.userName == 'Sys'"
                class="nav-item"
              >
                <router-link :to="{ name: 'PlanForm' }" class="nav-link">
                  <span>
                    <i class="fas fa-file-invoice-dollar"></i>
                    <span class="nav-label">Planos</span>
                  </span>
                </router-link>
              </li>

              <li class="nav-item">
                <router-link
                  :to="{ name: 'SubscriptionForm' }"
                  class="nav-link"
                >
                  <span>
                    <i class="fas fa-credit-card"></i>
                    <span class="nav-label">Assinaturas</span>
                  </span>
                </router-link>
              </li>

              <li class="nav-item">
                <router-link :to="{ name: 'PaymentForm' }" class="nav-link">
                  <span>
                    <i class="fas fa-receipt"></i>
                    <span class="nav-label">Pagamentos</span>
                  </span>
                </router-link>
              </li>

              <li
                v-if="
                  userStore.currentUser?.userName == 'Sys' &&
                  userStore.currentUser?.loginContext !== 'managed'
                "
                class="nav-item"
              >
                <router-link :to="{ name: 'CouponForm' }" class="nav-link">
                  <span>
                    <i class="fas fa-ticket-alt"></i>
                    <span class="nav-label">Cupons</span>
                  </span>
                </router-link>
              </li>

              <li
                v-if="
                  userStore.currentUser?.userName == 'Sys' &&
                  userStore.currentUser?.loginContext !== 'managed'
                "
                class="nav-item"
              >
                <router-link :to="{ name: 'CouponUserForm' }" class="nav-link">
                  <span>
                    <i class="fas fa-user-tag"></i>
                    <span class="nav-label">Usuário-Cupom</span>
                  </span>
                </router-link>
              </li>

              <li
                v-if="
                  userStore.currentUser?.userName == 'Sys' &&
                  userStore.currentUser?.loginContext !== 'managed'
                "
                class="nav-item"
              >
                <router-link
                  :to="{ name: 'CouponReservationForm' }"
                  class="nav-link"
                >
                  <span>
                    <i class="fas fa-bookmark"></i>
                    <span class="nav-label">Cupom-Reserva</span>
                  </span>
                </router-link>
              </li>

              <li
                v-if="
                  userStore.currentUser?.userName == 'Sys' &&
                  userStore.currentUser?.loginContext !== 'managed'
                "
                class="nav-item"
              >
                <router-link :to="{ name: 'users' }" class="nav-link">
                  <span>
                    <i class="fas fa-users"></i>
                    <span class="nav-label">Usuários</span>
                  </span>
                </router-link>
              </li>

              <!-- Montar turma é parte da delegação: sem Grupos e Usuário-Grupo,
                   o professor cadastra o aluno mas não consegue organizá-lo. -->
              <li v-if="canManageCircle" class="nav-item">
                <router-link :to="{ name: 'groups' }" class="nav-link">
                  <span>
                    <i class="fas fa-users-cog"></i>
                    <span class="nav-label">Grupos</span>
                  </span>
                </router-link>
              </li>

              <li
                v-if="
                  userStore.currentUser?.userName == 'Sys' &&
                  userStore.currentUser?.loginContext !== 'managed'
                "
                class="nav-item"
              >
                <router-link :to="{ name: 'permissions' }" class="nav-link">
                  <span>
                    <i class="fas fa-key"></i>
                    <span class="nav-label">Permissões</span>
                  </span>
                </router-link>
              </li>

              <li v-if="canManageCircle" class="nav-item">
                <router-link :to="{ name: 'user-group' }" class="nav-link">
                  <span>
                    <i class="fas fa-link"></i>
                    <span class="nav-label">Usuário-Grupo</span>
                  </span>
                </router-link>
              </li>

              <!-- Membro gerenciado só vê "Membros" se o vínculo delegar a
                   administração do círculo do titular (professor/administrador). -->
              <li v-if="canManageCircle" class="nav-item">
                <router-link
                  :to="{ name: 'MemberAccountForm' }"
                  class="nav-link"
                >
                  <span>
                    <i class="fas fa-user-friends"></i>
                    <span class="nav-label">Membros</span>
                  </span>
                </router-link>
              </li>
            </ul>
          </li>

          <li class="nav-item">
            <router-link :to="{ name: 'AuthorForm' }" class="nav-link">
              <span>
                <i class="fas fa-pen-nib"></i>
                <span class="nav-label">Autores</span>
              </span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link :to="{ name: 'CategoryForm' }" class="nav-link">
              <span>
                <i class="fas fa-book-open"></i>
                <span class="nav-label">Gêneros</span>
              </span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link :to="{ name: 'TopicForm' }" class="nav-link">
              <span>
                <i class="fas fa-lightbulb"></i>
                <span class="nav-label">Assuntos</span>
              </span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link :to="{ name: 'TagForm' }" class="nav-link">
              <span>
                <i class="fas fa-hashtag"></i>
                <span class="nav-label">Tags de Nota</span>
              </span>
            </router-link>
          </li>

          <!-- A estante: a tela principal de textos. -->
          <li class="nav-item">
            <router-link :to="{ name: 'TextCardsForm' }" class="nav-link">
              <span>
                <i class="fas fa-book"></i>
                <span class="nav-label">Estante</span>
              </span>
            </router-link>
          </li>

          <!-- Mesma listagem, em tabela e com o painel de busca por campo —
               mesmo destaque de "pesquisa" que Atividades e Notas usam. -->
          <li class="nav-item">
            <router-link
              :to="{ name: 'TextForm' }"
              class="nav-link nav-link--search"
            >
              <span>
                <span class="nav-search-icon-wrap">
                  <i class="fas fa-book"></i>
                  <i class="fas fa-search nav-search-magnifier"></i>
                </span>
                <span class="nav-label">
                  Textos
                  <span class="nav-search-badge">pesquisa</span>
                </span>
              </span>
            </router-link>
          </li>

          <!-- ✅ MUDANÇA: item Atividades com destaque visual de "Pesquisa" -->
          <li class="nav-item">
            <router-link
              :to="{ name: 'ActivityGeneralForm' }"
              class="nav-link nav-link--search"
            >
              <span>
                <span class="nav-search-icon-wrap">
                  <i class="fas fa-tasks"></i>
                  <i class="fas fa-search nav-search-magnifier"></i>
                </span>
                <span class="nav-label">
                  Atividades
                  <span class="nav-search-badge">pesquisa</span>
                </span>
              </span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link
              :to="{ name: 'NoteGeneralForm' }"
              class="nav-link nav-link--search nav-link--notes-search"
            >
              <span>
                <span class="nav-search-icon-wrap">
                  <i class="fas fa-sticky-note"></i>
                  <i class="fas fa-search nav-search-magnifier"></i>
                </span>
                <span class="nav-label">
                  Notas
                  <span class="nav-search-badge">pesquisa</span>
                </span>
              </span>
            </router-link>
          </li>
          <li v-if="userStore.currentUser?.userName === 'Sys'" class="nav-item">
            <router-link
              :to="{ name: 'AiNoteGeneratorForm' }"
              class="nav-link nav-link--ai"
            >
              <span>
                <span class="nav-ai-icon-wrap">
                  <i class="fas fa-robot"></i>
                  <i class="fas fa-wand-magic-sparkles nav-ai-sparkle"></i>
                </span>

                <span class="nav-label">
                  Notas com IA
                  <span class="nav-ai-badge">beta</span>
                </span>
              </span>
            </router-link>
          </li>
        </ul>
      </nav>

      <a href="#" class="logout" @click.prevent="logout">
        <i class="fas fa-sign-out-alt"></i>
        <span class="nav-label">Sair</span>
      </a>
    </aside>

    <div
      class="main-panel"
      :class="{ 'layout-split-content': isRightAreaRendered }"
    >
      <header class="header">
        <div class="user-controls">
          <div class="support-actions">
            <!-- Fale com o fundador: WhatsApp direto, com nome, e-mail e tela -->
            <a
              :href="founderWhatsAppUrl"
              target="_blank"
              rel="noopener"
              class="support-help-btn support-help-btn--whatsapp"
              title="Fale com o fundador"
              aria-label="Fale com o fundador pelo WhatsApp"
            >
              <i class="fab fa-whatsapp"></i>
            </a>

            <!-- Atualizar Plano: mesmo destino do item do menu do avatar -->
            <button
              v-if="userStore.currentUser?.loginContext !== 'managed'"
              type="button"
              class="support-help-btn support-help-btn--upgrade"
              title="Atualizar Plano"
              aria-label="Atualizar Plano"
              @click="openUpgradeModal"
            >
              <i class="fas fa-rocket"></i>
            </button>

            <!-- Sino: convites para entrar num círculo e respostas a eles -->
            <NotificationBell />

            <!-- ✅ NOVO: Fale conosco (oculto por ora; o WhatsApp do fundador
                 é o canal de suporte. Para reexibir, remova o v-if.) -->
            <button
              v-if="false"
              type="button"
              class="support-help-btn"
              @click="openSupportEmailModal"
            >
              <i class="fas fa-headset"></i>
              <span class="support-help-label"></span>
            </button>
          </div>

          <div class="user-info">
            <span class="user-name">{{ userStore.currentUser?.userName }}</span>

            <span
              v-if="
                userPlanName &&
                userStore.currentUser?.loginContext !== 'managed'
              "
              class="user-plan"
              :class="userPlanClass"
            >
              {{ userPlanName }}
            </span>

            <!-- <span
              v-else-if="userStore.currentUser?.loginContext === 'managed'"
              class="user-plan"
              :class="userPlanClass"
            >
              👥 {{ userPlanName }}
            </span> -->

            <!-- contexto managed — badge de membro -->
            <!-- contexto managed — badge de membro enriquecido -->
            <span
              v-else-if="userStore.currentUser?.loginContext === 'managed'"
              class="user-plan user-plan--managed"
              :title="managedMemberHint"
            >
              <i class="fas fa-user-friends user-plan--managed-icon"></i>
              <span class="user-plan--managed-text">
                Membro de
                <strong class="user-plan--managed-name">{{
                  userStore.currentUser?.managerName
                }}</strong>
              </span>
            </span>
          </div>

          <!-- ✅ MUDANÇA: wrapper do avatar + chevron para indicar dropdown -->
          <div
            class="user-profile-trigger"
            @click="toggleDropdown"
            ref="dropdownRef"
            :class="{ 'user-profile-trigger--open': isDropdownOpen }"
          >
            <div class="user-profile">
              <!-- Foto real, se houver -->
              <img
                v-if="userAvatarUrl"
                :src="userAvatarUrl"
                alt="Foto do usuário"
                class="user-profile-img"
              />

              <!-- Fallback: ícone centralizado -->
              <div v-else class="user-profile-fallback">
                <i class="fas fa-user" aria-hidden="true"></i>
              </div>
            </div>

            <!-- ✅ MUDANÇA: chevron à direita do avatar -->
            <i
              class="fas fa-chevron-down user-profile-chevron"
              :class="{ 'user-profile-chevron--open': isDropdownOpen }"
              aria-hidden="true"
            ></i>
          </div>

          <div
            v-if="isDropdownOpen"
            class="user-dropdown user-dropdown--actions"
          >
            <div class="user-dropdown__inner">
              <!-- itens do dropdown permanecem como estão -->
              <a
                href="#"
                class="user-dropdown__item"
                @click.prevent="openAccountModal"
              >
                <i class="fas fa-user-circle"></i>
                <span>Perfil</span>
              </a>

              <a
                href="#"
                class="user-dropdown__item"
                @click.prevent="openNoteStyleSettingsModal"
              >
                <i class="fas fa-cog"></i>
                <span>Preferências</span>
              </a>

              <a
                :href="founderWhatsAppUrl"
                target="_blank"
                rel="noopener"
                class="user-dropdown__item user-dropdown__item--whatsapp"
                @click="isDropdownOpen = false"
              >
                <i class="fab fa-whatsapp"></i>
                <span>Fale com o fundador</span>
              </a>

              <router-link
                :to="{ name: 'HomePage' }"
                class="user-dropdown__item"
                @click="isDropdownOpen = false"
              >
                <i class="fas fa-globe"></i>
                <span>Site público</span>
              </router-link>

              <a
                v-if="userStore.currentUser?.loginContext !== 'managed'"
                href="#"
                class="user-dropdown__item user-dropdown__item--upgrade"
                @click.prevent="openUpgradeModal"
              >
                <i class="fas fa-rocket"></i>
                <span>Atualizar Plano</span>
              </a>

              <a
                v-if="userStore.currentUser?.userName === 'Sys'"
                href="#"
                class="user-dropdown__item"
                @click.prevent="toggleTheme"
              >
                <i :class="isDark ? 'fas fa-sun' : 'fas fa-moon'"></i>
                <span>{{ isDark ? "Tema claro" : "Tema escuro" }}</span>
              </a>

              <div class="user-dropdown__separator"></div>

              <a
                href="#"
                class="user-dropdown__item user-dropdown__item--danger"
                @click.prevent="logout"
              >
                <i class="fas fa-sign-out-alt"></i>
                <span>Sair</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      <router-view v-if="isRightAreaRendered"></router-view>

      <div v-else class="content-area">
        <router-view></router-view>
      </div>
    </div>
  </div>

  <!-- ✅ MUDANÇA:
       - :upgrade-modal-closed-signal conecta o contador antes solto.
       - @subscriptionChanged recarrega as stores quando o titular cancela/
         pausa/reativa/estorna a assinatura na aba Resumo da Conta, mantendo
         o badge de plano do header sincronizado. -->
  <AccountSettingsModal
    v-if="showAccountModal"
    :suspend-keyboard-shortcuts="showUpgradeModal"
    :upgrade-modal-closed-signal="upgradeModalClosedSignal"
    @close="showAccountModal = false"
    @openUpgradeModal="openUpgradeModal"
    @subscriptionChanged="handleSubscriptionChanged"
  />

  <SettingsModal v-if="showSettingsModal" @close="showSettingsModal = false" />

  <!-- ✅ CORRIGIDO:
     Não usar .stop em eventos customizados de componente.
     O .stop só deve ser usado em eventos DOM, como @click.stop. -->
  <UpgradePage
    v-if="showUpgradeModal"
    :initial-step="2"
    :redirect-on-catalog-close="false"
    @close-upgrade-modal="handleCloseUpgradeModal"
    @close="handleCloseUpgradeFlow"
  />

  <!-- ✅ NOVO: modal de Ajuda & Sugestões -->
  <SupportEmailModal
    v-if="showSupportEmailModal"
    @close="showSupportEmailModal = false"
    @submit="handleSupportEmailSubmit"
  />

  <FounderWelcomeModal
    v-if="showFounderWelcome"
    :first-name="founderFirstName"
    :whatsapp-url="founderWhatsAppUrl"
    @close="dismissFounderWelcome"
  />

  <SubscriptionExpiredModal
    v-if="showSubscriptionExpiredModal"
    :show="showSubscriptionExpiredModal"
    :message="cachedExpiredMessage"
    @close="showSubscriptionExpiredModal = false"
  />

  <RecurringPaymentRejectedModal
    v-if="showRecurringRejectedModal"
    :show="showRecurringRejectedModal"
    :plan-name="subscriptionStatusStore.previousPlanName"
    :is-managed="userStore.currentUser?.loginContext === 'managed'"
    @close="handleRecurringRejectedClose"
  />

  <!-- TOAST – mesmo padrão visual do AddPermissionForm.vue -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Aviso de assinatura" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style>
/* Tokens e fonte vêm de src/assets/tokens.css; ícones/fonte de public/index.html. */

html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
}

#vue-app-container {
  display: flex;
  flex-direction: row;
  height: 100vh;
  background-color: var(--bg-main);
  font-family: var(--font-family);
  color: var(--text-dark);
  box-sizing: border-box;
}

.sidebar-left {
  position: relative;
  flex-basis: 260px;
  flex-shrink: 0;
  background-color: var(--bg-sidebar-left);
  border-radius: 16px;
  padding: 0;
  padding-top: 8px;
  padding-bottom: 0;
  margin: 8px 0px 8px 8px;
  display: flex;
  flex-direction: column;

  /* ✅ A sidebar em si não rola.
     A rolagem continua restrita em .sidebar-nav-scroll. */
  overflow: hidden;

  transition: flex-basis 0.25s ease, width 0.25s ease;
}

/* topo fixo da sidebar: não participa da rolagem */

.sidebar-brand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 12px 14px 16px;
  flex-shrink: 0;
}

.sidebar-brand-link {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--text-dark);
  min-width: 0;
}

.sidebar-brand-logo {
  width: 36px;
  height: 36px;
  object-fit: contain;
  flex-shrink: 0;
}

.sidebar-brand-name {
  font-size: 1.3rem;
  font-weight: 700;
  white-space: nowrap;
}

.sidebar-left--collapsed .sidebar-brand-name {
  display: none;
}

.sidebar-left--collapsed .sidebar-brand {
  justify-content: center;
}

/* área rolável da árvore do menu */
.sidebar-nav-scroll {
  flex-grow: 1;
  min-height: 0;

  /* ✅ mantém a rolagem apenas do menu */
  overflow-y: auto;
  overflow-x: hidden;
}

.sidebar-left--collapsed {
  flex-basis: 72px;
}

/* quando colapsado, links mais centralizados */
.sidebar-left--collapsed .nav-link {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}

/* esconde texto dos labels, mantém só ícones */
.sidebar-left--collapsed .nav-label {
  display: none;
}

/* remove indentação dos sub-itens ao colapsar */
.sidebar-left--collapsed .sub-nav {
  padding-left: 0;
}

/* esconde seta de dropdown quando colapsado */
.sidebar-left--collapsed .dropdown-arrow {
  display: none;
}

.main-panel {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 12px;
  padding-top: 72px;
  gap: 12px;
  min-width: 0;
  min-height: 0;
}

/* ✅ 65 / 35 (main-content / sidebar-right) */
.layout-split-content {
  display: grid;
  grid-template-columns: 65fr 35fr;
  grid-template-rows: 52px 1fr;
  padding: 0 8px 8px 8px;
  gap: 8px;
  height: 100%;
  min-height: 0;
}

.header {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 20;
  background: var(--bg-main);
  border-radius: 0 0 0 12px;
  padding: 6px 24px;
  display: flex;
  align-items: center;
  box-shadow: none;
}

.layout-split-content .header {
  grid-column: 2;
  grid-row: 1;
  margin: 0;
  max-width: none;
}

.layout-split-content .main-content {
  grid-column: 1;
  grid-row: 1 / span 2;
  padding-left: 0px;
  margin-top: 8px;
}
.layout-split-content .sidebar-right {
  grid-column: 2;
  grid-row: 2;
  min-height: 0;
  margin-top: 8px;
}

.content-area {
  flex-grow: 1;
  overflow-y: auto;
  min-height: 0;
}

/* =========================
   ✅ SEARCH / dimensões
   ========================= */
.search-container {
  min-width: 300px;
  flex-grow: 1;
  max-width: 50%;
}

/* === Header user info === */
.user-controls {
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
}

/* Fale com o fundador + Fale conosco, lado a lado */
.support-actions {
  display: inline-flex;
  align-items: center;
  align-self: flex-end;
  gap: 0.4rem;
}

a.support-help-btn {
  text-decoration: none;
}

.support-help-btn.support-help-btn--whatsapp {
  background-color: var(--whatsapp);
  color: var(--text-on-whatsapp);
}

/* Mesmo tamanho do sino ao lado (NotificationBell: ícone 0.9rem, botão 40x24). */
.support-help-btn.support-help-btn--whatsapp i,
.support-help-btn.support-help-btn--upgrade i {
  font-size: 0.9rem;
  /* Largura fixa: o foguete é mais largo que o WhatsApp e o sino, e o botão
     dele ficaria maior. */
  width: 0.9rem;
  text-align: center;
}

.support-help-btn.support-help-btn--whatsapp:hover {
  background-color: var(--whatsapp-hover);
}

.support-help-btn.support-help-btn--upgrade {
  background-color: var(--upgrade-bg);
  color: var(--upgrade-icon);
}

.support-help-btn.support-help-btn--upgrade:hover {
  background-color: var(--upgrade-bg-hover);
}

/* ✅ NOVO: botão Ajuda & Sugestões na topbar */
.support-help-btn {
  display: inline-flex;
  align-items: center;
  align-self: flex-end;
  gap: 0.35rem;
  padding: 0.3rem 0.8rem;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;

  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
  box-shadow: none;

  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.support-help-btn i {
  font-size: 0.9rem;
}

.support-help-btn:hover {
  background-color: var(--brand-soft-hover);
}

.support-help-btn:active {
  transform: translateY(1px);
}

.support-help-btn:focus,
.support-help-btn:focus-visible {
  outline: none;
  box-shadow: none;
}

.support-help-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.support-help-btn:disabled:hover {
  transform: none;
  box-shadow: none;
}
.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.user-name {
  font-size: 0.9rem;
  font-weight: bold;
  line-height: 1.2;
}

/* ✅ Chip plano */
.user-plan {
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.2;
  display: inline-block;
  margin-top: 4px;
}

/* ✅ cores por plano */
.btn-pro {
  background: #ffd6a5;
  color: #6a4b1f;
}

.btn-plus {
  background: var(--brand-light);
  color: #2a2457;
}

.btn-free {
  background: #d1e8d1;
  color: #215921;
  border-color: #9fd39f;
}

.btn-premium {
  background: #ffadad;
  color: #7a1f1f;
}

/* =========================================================
   ✅ TRIGGER DO AVATAR + CHEVRON DO DROPDOWN
   ========================================================= */
.user-profile-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.user-profile-trigger:active {
  transform: translateY(1px);
}

.user-profile-trigger:focus,
.user-profile-trigger:focus-visible {
  outline: none;
  box-shadow: none;
}

.user-profile-trigger:disabled:hover {
  transform: none;
  box-shadow: none;
}

.user-profile-chevron {
  font-size: 0.8rem;
  color: var(--text-light);
  transition: transform 0.2s ease, color 0.2s ease;
}

.user-profile-trigger:hover .user-profile-chevron {
  color: var(--text-dark);
}

.user-profile-chevron--open {
  transform: rotate(180deg);
}

/* Container do avatar no header */
.user-profile {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  overflow: hidden;

  /* ✅ MUDANÇA: cursor fica no wrapper .user-profile-trigger */
  display: flex;
  align-items: center;
  justify-content: center;

  background-color: var(--neutral-100);
  box-sizing: border-box;
  padding: 0;
}

/* Foto real */
.user-profile-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Fallback quando não há foto */
.user-profile-fallback {
  width: 100%;
  height: 100%;

  /* flex também aqui pra garantir centralização do ícone */
  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 12px;
  box-sizing: border-box;

  /* mesmas cores de fallback usadas na DashboardView */
  background-color: var(--border-color); /* bg-slate-200 */
}

/* Ícone dentro do fallback – cinza e CENTRALIZADO */
.user-profile-fallback i {
  font-size: 20px;
  color: var(--text-muted); /* text-slate-500 */
  line-height: 1;
  display: block;
  margin: 0;
}

/* =========================================================
   ✅ DROPDOWN
   ========================================================= */
.user-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 100;
  min-width: 180px;
  width: max-content;
  background-color: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-dropdown);
  overflow: hidden;
  padding: 0;
}

.user-dropdown__inner {
  padding: 0.25rem 0;
}

.user-dropdown__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.4rem 0.65rem;
  font-size: 0.8rem;
  line-height: 1.1;
  color: var(--text-dark);
  text-decoration: none;
  white-space: nowrap;
  font-weight: 500;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.user-dropdown__item i {
  width: 16px;
  text-align: center;
  font-size: 0.9em;
  color: var(--text-light);
}

.user-dropdown__item:hover {
  background-color: var(--surface-hover);
}

.user-dropdown__item:active {
  transform: translateY(1px);
}

.user-dropdown__item:focus,
.user-dropdown__item:focus-visible {
  outline: none;
  box-shadow: none;
}

.user-dropdown__item:disabled:hover {
  transform: none;
  box-shadow: none;
}

.user-dropdown__separator {
  height: 1px;
  background-color: var(--border-color);
  margin: 0.35rem 0.65rem;
}

.user-dropdown__item--danger {
  color: var(--text-important);
}

.user-dropdown__item--danger i {
  color: var(--text-important);
}

.user-dropdown__item--danger:hover {
  background-color: var(--danger-hover-bg);
}

/* ===== Sidebar/Nav ===== */
.nav {
  list-style: none;
  padding: 0;
  margin: 0;
  flex-grow: 1;
}
.nav-item {
  margin-bottom: 8px;
}

.sidebar-toggle-btn--inline {
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  color: #7a1f1f;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background-color 0.2s ease;
}

.sidebar-toggle-btn--inline:hover {
  background-color: var(--bg-active-nav);
}
.nav-link {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-radius: 12px;
  text-decoration: none;
  color: var(--text-dark);
  font-weight: 500;
  transition: background-color 0.3s ease;
  font-size: 0.85rem;
}
.nav-link span {
  display: flex;
  align-items: center;
  gap: 12px;
}
.nav-link:hover,
.nav-link.active {
  background-color: var(--bg-active-nav);
}
.dropdown-arrow {
  transition: transform 0.3s ease;
}
.dropdown-arrow.collapsed {
  transform: rotate(-90deg);
}
.sub-nav {
  list-style: none;
  padding: 8px 0 0 28px;
  margin: 0;
}

/* 🎯 Ícones de subnível em cor diferente */
.sub-nav .nav-link i {
  color: var(--nested-icon-color);
}

/* 🎯 Quando colapsado, destaca ainda mais os ícones aninhados */
.sidebar-left--collapsed .sub-nav .nav-link i {
  color: var(--nested-icon-color-collapsed);
}

/* ══════════════════════════════════════════════════════
   🔍 NAV — item "Atividades (Pesquisa)" destacado
   ══════════════════════════════════════════════════════ */

/* Fundo levemente tingido de lilás para diferenciar do nav-link comum */
.nav-link--search {
  background: linear-gradient(
    90deg,
    rgba(189, 178, 255, 0.18) 0%,
    transparent 100%
  );
  border-left: 3px solid var(--brand-light);
  padding-left: 13px; /* compensa a borda para manter alinhamento */
}

.nav-link--search:hover,
.nav-link--search.router-link-active {
  background: linear-gradient(
    90deg,
    rgba(189, 178, 255, 0.35) 0%,
    rgba(189, 178, 255, 0.08) 100%
  );
}

/* Wrapper dos dois ícones sobrepostos */
.nav-search-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

/* Ícone principal fa-tasks — levemente menor para dar espaço ao lupa */
.nav-search-icon-wrap .fa-tasks {
  font-size: 0.95em;
  color: var(--text-brand);
}

/* Lupa pequena posicionada no canto inferior direito do ícone principal */
.nav-search-magnifier {
  position: absolute;
  bottom: -3px;
  right: -5px;
  font-size: 0.52em;
  color: var(--brand-light);
  background: var(--bg-sidebar-left);
  border-radius: 50%;
  padding: 1px;
  line-height: 1;
}

/* Badge "pesquisa" inline — pill compacto após o label */
.nav-search-badge {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--brand-light), var(--brand));
  color: #1a0f4f;
  vertical-align: middle;
  line-height: 1.5;
  /* pisca suavemente para chamar atenção */
  animation: badge-pulse 3s ease-in-out infinite;
}

/* sidebar colapsada: esconde badge e lupa (ficam ocultos junto com nav-label) */
.sidebar-left--collapsed .nav-search-badge {
  display: none;
}

.sidebar-left--collapsed .nav-search-magnifier {
  font-size: 0.6em;
  bottom: -2px;
  right: -4px;
}

@keyframes badge-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(0.94);
  }
}

/* 🔘 Botão de colapsar/expandir integrado à sidebar */
.sidebar-toggle-btn {
  border: none;
  outline: none;
  cursor: pointer;

  padding: 6px 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  /* ✅ mesmo fundo da sidebar */
  background: var(--bg-sidebar-left);

  /* ✅ sem borda/sombra fixa */
  box-shadow: none;

  color: #7a1f1f;
  font-size: 0.9rem;

  transition: background-color 0.15s ease, transform 0.15s ease,
    box-shadow 0.15s ease;
}

/* ✅ Ícones do botão combinando com a paleta (não preto) */
.sidebar-toggle-btn i {
  color: #7a1f1f;
}

.sidebar-toggle-btn:hover {
  background: var(--bg-active-nav);
  transform: translateY(-1px) scale(1.03);
  box-shadow: 0 2px 6px rgba(122, 31, 31, 0.12);
}

.sidebar-toggle-btn:active {
  transform: translateY(0) scale(0.97);
  box-shadow: 0 0 10px rgba(189, 178, 255, 0.7);
}

/* versão do topo: sempre quadrado com radius 10 */
.sidebar-toggle-btn--top {
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  padding: 0;
  border-radius: 10px;
}

.logout {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: var(--text-dark);
  font-weight: 500;
  padding: 12px 16px;
  margin-top: auto;
  cursor: pointer;
}

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 4px;
}
::-webkit-scrollbar-thumb:hover {
  background: #c7c7c7;
}

/* =========================================
   ⭐ Destaque: Atualizar Plano
   ========================================= */
.user-dropdown__item--upgrade {
  background-color: var(--upgrade-bg);
  color: var(--upgrade-text);
  font-weight: 600;
  margin: 2px 6px;
  border-radius: 8px;
  /* A margem lateral de 6px que forma a "pílula" empurrava o ícone 6px para
     dentro em relação aos outros itens; a folga interna compensa para o ícone
     ficar na mesma coluna. */
  padding-left: calc(0.65rem - 6px);
  padding-right: calc(0.65rem - 6px);
}

.user-dropdown__item--upgrade i {
  color: var(--upgrade-icon);
}

/* Fale com o fundador: só o ícone na cor do WhatsApp */
.user-dropdown__item--whatsapp i {
  color: var(--whatsapp);
}

.user-dropdown__item--upgrade:hover {
  background-color: var(--upgrade-bg-hover);
}

/* Ícone fallback com borda igual ao avatar */
/* Fallback do avatar do usuário – cores iguais às usadas na DashboardView */
.user-profile-icon {
  width: 100%;
  height: 100%;
  display: flex; /* centralizado dentro do container */
  align-items: center;
  justify-content: center;

  font-size: 20px;
  border-radius: 12px;
  box-sizing: border-box;

  /* 🎨 Mesmas cores do fallback da DashboardView */
  background-color: var(--border-color); /* bg-slate-200 */
  color: var(--text-muted); /* text-slate-500 */
}

/* Fallback quando não há foto: mesmo tamanho do avatar, ícone centralizado */
/* Fallback quando não há foto: mesmo tamanho do avatar, ícone centralizado
   e mesma paleta do fallback da DashboardView.vue */
.user-profile-fallback {
  width: 40px;
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: var(--border-color); /* bg-slate-200 */
  border-radius: 12px;
  border: 2px solid var(--border-color);

  box-sizing: border-box;
  padding: 0; /* garante nada empurrando o ícone */
}

/* Ícone dentro do fallback — mesma cor da DashboardView (text-slate-500) */
.user-profile-icon {
  font-size: 20px;
  color: var(--text-muted); /* text-slate-500 */
  line-height: 1;

  display: flex;
  align-items: center;
  justify-content: center;
}

.toast {
  width: 420px;
}

/* ✅ NOVO: item "Notas (Pesquisa)" — mesma estrutura do Atividades,
   mas com tonalidade azul-clara para diferenciar */
.nav-link--notes-search {
  background: linear-gradient(
    90deg,
    rgba(160, 196, 255, 0.18) 0%,
    transparent 100%
  );
  border-left: 3px solid var(--focus-border);
  padding-left: 13px;
}

.nav-link--notes-search:hover,
.nav-link--notes-search.router-link-active {
  background: linear-gradient(
    90deg,
    rgba(160, 196, 255, 0.35) 0%,
    rgba(160, 196, 255, 0.08) 100%
  );
}

.nav-link--ai {
  background: linear-gradient(
    90deg,
    rgba(189, 178, 255, 0.22) 0%,
    transparent 100%
  );
  border-left: 3px solid var(--brand);
  padding-left: 13px;
}

.nav-link--ai:hover,
.nav-link--ai.router-link-active {
  background: linear-gradient(
    90deg,
    rgba(189, 178, 255, 0.4) 0%,
    rgba(189, 178, 255, 0.1) 100%
  );
}

.nav-ai-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.nav-ai-icon-wrap .fa-robot {
  font-size: 0.95em;
  color: var(--text-brand);
}

.nav-ai-sparkle {
  position: absolute;
  bottom: -3px;
  right: -5px;
  font-size: 0.5em;
  color: var(--brand);
  background: var(--bg-sidebar-left);
  border-radius: 50%;
  padding: 1px;
  line-height: 1;
}

.nav-ai-badge {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--brand), var(--text-brand));
  color: #fff;
  vertical-align: middle;
  line-height: 1.5;
}
/* ── Chip: Membro de ── */
.user-plan--managed {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  /* ~22 caracteres ("Membro de Colégio Est…"); o nome inteiro fica no title. */
  max-width: 188px;
  background: var(--warning-bg);
  color: var(--warning-text);
  border: 1px solid #fcd34d;
  border-radius: 999px;
  padding: 3px 10px 3px 8px;
  white-space: nowrap;
  overflow: hidden;
}

.user-plan--managed-icon {
  flex-shrink: 0;
  font-size: 0.8rem;
  color: #b45309;
}

.user-plan--managed-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.78rem;
  font-weight: 500;
}

.user-plan--managed-name {
  font-weight: 700;
  color: #78350f;
}
</style>

